import * as Yup from 'yup';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddressInfoForm from './AddressInfoForm';
import { Grid, Alert, Divider } from '@mui/material';
import useI18nForm from '../../hooks/useI18nForm';
import useAuth0 from 'common/hooks/useAuth0';
import { Address } from 'pages/address/Address';
import { formatPostalCodeValue, FormatDefaultValuePhoneNumber, UnFormatValuePhoneNumber } from '../../FieldFormat';
import config from '../../../../config';
import { UpsertAddressFLS } from 'services/AddressService';
import FormButtons from 'components/common/Buttons/FormButtons';
import { ProvinceValidation, phoneValidationRegex, postalCodeValidationRegex } from 'components/common/regex';
import useApi, { Status } from 'common/hooks/useApi';
import { GetProfile } from 'services/profile/ProfileService';
import LoadingIndicator from 'components/common/LoadingIndicator';

const redirect = (lang: string, udpateCommerce: boolean) => {
  const url = config.ADDRESSES_REDIRECT.replace('{lang}', lang) + '?updated=' + udpateCommerce;
  const win: Window = window;
  if (win.parent) {
    win.parent.location = url;
  } else {
    win.location = url;
  }
};

const AddressForm = (props: { address?: Address | null }): JSX.Element => {
  const { t, i18n } = useTranslation('component');
  const { user } = useAuth0();
  const [fetchProfile, profile, fetchingProfileStatus] = useApi(GetProfile); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [errorMessages, setErrorMessages] = useState({ hasError: false, errorMessage: '' });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const initialValues = {
    AddressLabel: props.address?.Label || '',
    AddressFirstName: props.address?.FirstName || '',
    AddressLastName: props.address?.LastName || '',
    AddressPhoneNumber: FormatDefaultValuePhoneNumber(props.address?.Phone || ''),
    CivicNumber: props.address?.CivicNumber || '',
    Street: props.address?.Street || '',
    Apartment: props.address?.Appartment || '',
    City: props.address?.City || '',
    Province: props.address?.Province || 'default',
    Country: 'Canada',
    PostalCode: props.address?.PostalCode ? formatPostalCodeValue(props.address.PostalCode) : '',
    IsPrimaryAddress: props.address?.AddressType == 'Primary' || false,
  };

  const isDataLoading = fetchingProfileStatus === Status.Pending;
  const hasFetchDataFailed = fetchingProfileStatus === Status.Rejected;
  const isDataResolved = fetchingProfileStatus === Status.Resolved;

  useEffect(() => {
    // Fetch user data when component mounts
    if (user?.session_id) {
      fetchProfile(user?.session_id);
    }
  }, [fetchProfile, user?.session_id]);

  const PreviousSubmit = (): any => {
    redirect(i18n.resolvedLanguage ?? '', false);
  };

  const validationSchema = Yup.object().shape({
    AddressLabel: Yup.string().max(
      50,
      t('FormValidations.FieldTooLong', {
        field: t('Form.AddressLabel').toString().toLocaleLowerCase(),
        length: 50,
      }).toString()
    ),
    AddressFirstName: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheM').toString() +
          ' ' +
          t('Form.FirstName').toString().toLocaleLowerCase() +
          ' ' +
          t('FormValidations.AssiciatedWithAddress').toString()
      )
      .max(
        250,
        t('FormValidations.FieldTooLong', {
          field: t('Form.FirstName').toString().toLocaleLowerCase(),
          length: 250,
        }).toString()
      ),
    AddressLastName: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheM').toString() +
          ' ' +
          t('Form.LastName').toString().toLocaleLowerCase() +
          ' ' +
          t('FormValidations.AssiciatedWithAddress').toString()
      )
      .max(
        250,
        t('FormValidations.FieldTooLong', {
          field: t('Form.LastName').toString().toLocaleLowerCase(),
          length: 250,
        }).toString()
      ),
    Street: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheF').toString() +
          ' ' +
          t('Form.Street').toString().toLocaleLowerCase()
      )
      .max(
        150,
        t('FormValidations.FieldTooLong', {
          field: t('Form.Street').toString().toLocaleLowerCase(),
          length: 150,
        }).toString()
      ),
    CivicNumber: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheM').toString() +
          ' ' +
          t('Form.CivicNumber').toString().toLocaleLowerCase()
      )
      .max(
        10,
        t('FormValidations.FieldTooLong', {
          field: t('Form.CivicNumber').toString().toLocaleLowerCase(),
          length: 10,
        }).toString()
      ),
    Apartment: Yup.string().max(
      10,
      t('FormValidations.FieldTooLong', {
        field: t('Form.Apartment').toString().toLocaleLowerCase(),
        length: 10,
      }).toString()
    ),
    City: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheF').toString() +
          ' ' +
          t('Form.City').toString().toLocaleLowerCase()
      )
      .max(
        100,
        t('FormValidations.FieldTooLong', {
          field: t('Form.City').toString().toLocaleLowerCase(),
          length: 100,
        }).toString()
      ),
    Country: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheM').toString() +
          ' ' +
          t('Form.Country').toString().toLocaleLowerCase()
      )
      .max(
        75,
        t('FormValidations.FieldTooLong', {
          field: t('Form.Country').toString().toLocaleLowerCase(),
          length: 75,
        }).toString()
      ),
    Province: Yup.string()
      .trim()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheF').toString() +
          ' ' +
          t('Form.Province').toString().toLocaleLowerCase()
      )
      .matches(
        ProvinceValidation,
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheF').toString() +
          ' ' +
          t('Form.Province').toString().toLocaleLowerCase()
      ),
    PostalCode: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheM').toString() +
          ' ' +
          t('Form.PostalCode').toString().toLocaleLowerCase()
      )
      .matches(
        postalCodeValidationRegex,
        t('FormValidations.InvalidField', {
          field: (i18n.resolvedLanguage == 'en'
            ? t('Form.PostalCode').toLocaleLowerCase()
            : t('Form.PostalCode')
          ).toString(),
        }).toString()
      ),
    AddressPhoneNumber: Yup.string()
      .required(
        t('FormValidations.PleaseEnter').toString() +
          ' ' +
          t('Prepositions.TheM').toString() +
          ' ' +
          t('Form.PhoneNumber').toString().toLocaleLowerCase() +
          ' ' +
          t('FormValidations.AssiciatedWithAddress').toString()
      )
      .matches(
        phoneValidationRegex,
        t('FormValidations.InvalidField', {
          field: (i18n.resolvedLanguage == 'en'
            ? t('Form.PhoneNumber').toLocaleLowerCase()
            : t('Form.PhoneNumber')
          ).toString(),
        }).toString()
      ),
  });

  const handleAddressUpsert = async function (values: any) {
    if (!isLoading) {
      setIsLoading(true);
      try {
        const address: Address = {
          Id: props.address?.Id || undefined,
          PostalCode: values.PostalCode,
          Province: values.Province,
          AddressType: values.address?.AddressType,
          IsPrimaryAddress: values.IsPrimaryAddress,
          Label: values.AddressLabel,
          FirstName: values.AddressFirstName,
          LastName: values.AddressLastName,
          CivicNumber: values.CivicNumber,
          Street: values.Street,
          Appartment: values.Apartment,
          City: values.City,
          Country: 'Canada',
          AddressPhoneNumber: UnFormatValuePhoneNumber(values.AddressPhoneNumber),
        };
        await UpsertAddressFLS(address, user!.session_id);
        redirect(i18n.resolvedLanguage ?? '', true);
      } catch (e) {
        setErrorMessages({ hasError: true, errorMessage: 'Errors.GenericError' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: (values: any) => {
      handleAddressUpsert(values);
    },
  });

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  return (
    <>
      {isDataLoading && <LoadingIndicator />}

      {hasFetchDataFailed && (
        <Alert id="fetchAlert" severity="error" sx={{ mb: '16px' }}>
          {t('Errors.GenericError')}
        </Alert>
      )}

      {isDataResolved && (
        <form noValidate onSubmit={formik.handleSubmit}>
          {errorMessages.hasError && (
            <Alert severity="error" sx={{ mt: '16px' }}>
              {t(errorMessages.errorMessage)}
            </Alert>
          )}
          <Grid mt={1} container spacing={2}>
            <AddressInfoForm formik={formik} />
          </Grid>
          <Divider />
          <FormButtons
            PrevButtonText={t('Buttons.Cancel')}
            SubmitButtonText={props.address == undefined ? t('Buttons.Add') : t('Buttons.Save')}
            PrevButtonHandler={PreviousSubmit}
            isLoading={isLoading}
          />
        </form>
      )}
    </>
  );
};

export default AddressForm;
